import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { ChakraProps, Flex, VStack } from '@chakra-ui/react';
import { AddressRec, GenderType } from '@netiva/classifieds-api';
import {
  FormEmailInput,
  FormInput,
  FormSelect,
  FormSwitch,
  isRequiredValueMissing,
  SelectOption,
} from '@netiva/classifieds-ui';

import { SupportedCountries } from '@/config';

const AddressInput = FormInput<AddressRec>;
const AddressEmailInput = FormEmailInput<AddressRec>;
const AddressSelect = FormSelect<AddressRec>;
const AddressFormSwitch = FormSwitch<AddressRec>;

export type AddressFormVariant = 'private' | 'company';

export type AddressFormProps = ChakraProps & {
  variant?: AddressFormVariant;
};

export const AddressForm: FC<AddressFormProps> = ({ variant, ...rest }) => {
  const { t } = useTranslation();

  const isPrivate = variant === 'private';
  const isCompany = variant === 'company';
  const showCompanyFields = !isPrivate;

  const { control, setValue, subscribe, trigger } = useFormContext<AddressRec>();
  const { company, firstname, lastname, mobile, phone, showMobile, type } = useWatch({ control });

  // revalidate company if name changes
  useEffect(() => {
    if (variant) return;
    return subscribe({
      formState: { values: true },
      callback: () => {
        trigger('company');
      },
    });
  }, [variant, subscribe, trigger]);

  // revalidate name fields if company changes
  useEffect(() => {
    if (variant) return;
    return subscribe({
      name: 'company',
      formState: { values: true },
      callback: ({ values: { company } }) => {
        if (company) {
          trigger('firstname');
          trigger('lastname');
        } else {
          setValue('homepageUrl', '');
        }
      },
    });
  }, [variant, setValue, subscribe, trigger]);

  // revalidate phone fields
  useEffect(() => {
    return subscribe({
      name: ['mobile', 'phone'],
      formState: { values: true },
      callback: ({ values: { mobile, phone } }) => {
        if (mobile) {
          trigger('phone');
        }
        if (phone) {
          trigger('mobile');
        }
      },
    });
  }, [subscribe, trigger]);

  // clear the visibility flags while their checkbox is disabled
  useEffect(() => {
    if (!mobile) {
      setValue('showMobile', false);
    }
    if (!mobile || !showMobile) {
      setValue('acceptsWhatsApp', false);
    }
    if (!phone) {
      setValue('showPhone', false);
    }
  }, [mobile, phone, setValue, showMobile]);

  const genderOptions = useMemo<SelectOption<GenderType>[]>(
    () => [
      { value: 'Unknown', label: t('address.form.gender.unkown') },
      { value: 'Male', label: t('address.form.gender.male') },
      { value: 'Female', label: t('address.form.gender.female') },
    ],
    [t]
  );

  const { t: countries } = useTranslation('countries');
  const countryOptions = useMemo(() => {
    const options = SupportedCountries.map<SelectOption>((code) => ({ value: code, label: countries(code) }));
    // sort: ch at the top, rest alphabetically
    options.sort((a, b) => (a.value === 'ch' ? -1 : b.value === 'ch' ? 1 : a.label.localeCompare(b.label)));
    return options;
  }, [countries]);

  const nameOrCompanyMessage = t('address.form.validation.nameOrCompany');
  const mobileOrPhoneMessage = t('address.form.validation.mobileOrPhone');

  return (
    <VStack maxWidth="500px" spacing={2} align="stretch" {...rest}>
      <AddressSelect
        name="gender"
        label={t('address.form.gender')}
        options={genderOptions}
        isRequired={type === 'Account'}
      />
      {showCompanyFields && (
        <Flex gap={2}>
          <AddressInput
            name="company"
            label={t('address.form.company')}
            isRequired={isCompany || !firstname || !lastname}
            validate={
              isCompany
                ? undefined
                : (value, object) =>
                    !isRequiredValueMissing(value) ||
                    (!isRequiredValueMissing(object.firstname) && !isRequiredValueMissing(object.lastname)) ||
                    nameOrCompanyMessage
            }
            flexBasis="50%"
          />
          <AddressInput name="company2" label={t('address.form.company2')} flexBasis="50%" />
        </Flex>
      )}

      <Flex gap={2}>
        <AddressInput
          name="firstname"
          label={t('address.form.firstName')}
          isRequired={isPrivate || !company}
          validate={
            isPrivate
              ? undefined
              : (value, object) =>
                  !isRequiredValueMissing(value) || !isRequiredValueMissing(object.company) || nameOrCompanyMessage
          }
          flexBasis="50%"
        />
        <AddressInput
          name="lastname"
          label={t('address.form.lastName')}
          isRequired={isPrivate || !company}
          validate={
            isPrivate
              ? undefined
              : (value, object) =>
                  !isRequiredValueMissing(value) || !isRequiredValueMissing(object.company) || nameOrCompanyMessage
          }
          flexBasis="50%"
        />
      </Flex>
      <Flex gap={2}>
        <AddressInput name="street" label={t('address.form.street')} isRequired />
        <AddressInput name="streetNo" label={t('address.form.streetNo')} maxWidth="110px" />
      </Flex>
      <AddressInput name="street2" label={t('address.form.street2')} />
      <Flex gap={2}>
        <AddressInput name="zip" label={t('address.form.zipCode')} isRequired maxWidth="90px" />
        <AddressInput name="city" label={t('address.form.city')} isRequired />
      </Flex>
      <AddressSelect
        name="countryIso2"
        label={t('address.form.country')}
        placeholder={t('address.form.selectCountry')}
        options={countryOptions}
        isRequired
        ignoreValueCase
      />
      <AddressEmailInput name="email" label={t('address.form.email')} isRequired={type === 'Account'} />
      <AddressInput
        name="mobile"
        label={t('address.form.mobile')}
        isRequired={type === 'Account' && !phone}
        validate={
          type !== 'Account'
            ? undefined
            : (value, object) =>
                !isRequiredValueMissing(value) || !isRequiredValueMissing(object.phone) || mobileOrPhoneMessage
        }
      />
      <AddressFormSwitch name="showMobile" label={t('address.form.showPhone')} isDisabled={!mobile} singleLine />
      <AddressFormSwitch
        name="acceptsWhatsApp"
        label={t('address.form.acceptsWhatsApp')}
        isDisabled={!showMobile}
        singleLine
      />
      <AddressInput
        name="phone"
        label={t('address.form.phone')}
        isRequired={type === 'Account' && !mobile}
        validate={
          type !== 'Account'
            ? undefined
            : (value, object) =>
                !isRequiredValueMissing(value) || !isRequiredValueMissing(object.mobile) || mobileOrPhoneMessage
        }
      />
      <AddressFormSwitch name="showPhone" label={t('address.form.showPhone')} isDisabled={!phone} singleLine />
      {showCompanyFields && !!company && <AddressInput name="homepageUrl" label={t('address.form.homepageUrl')} />}
    </VStack>
  );
};
